import React, { useMemo } from 'react';
import type { TableProps } from 'antd';
import { Select } from 'antd';
import type { ItemLibraryItem } from '../api/itemLibrary';
import { useTender } from '../contexts/TenderContext';
import { useItemLibrary } from '../hooks/useItemLibrary';
import { useDevMode } from '../contexts/DevModeContext';

/**
 * Dev-mode only: build a `resourceCode → nettRate` lookup from the templates
 * API (`/rate-engine/library/{region}/templates`) for displaying rates beneath
 * the resource code badge in table headers. The underlying React Query call
 * is gated on `showColumnCodes`, so in production (or with dev mode off) the
 * hook subscribes with `enabled: false` and the templates endpoint is NOT hit
 * by this feature. (Other features that need rates for calculation continue
 * to use `useItemLibrary` directly and trigger their own fetches as before.)
 */
function useDevModeCodeLibrary(): { rates: Map<string, number>; items: ItemLibraryItem[] } {
  const showColumnCodes = useDevMode();
  const { state } = useTender();
  const region = state?.region || '';
  const { data } = useItemLibrary(region, undefined, {
    enabled: showColumnCodes && !!region,
  });
  return useMemo(() => {
    const rates = new Map<string, number>();
    if (!showColumnCodes || !Array.isArray(data)) return { rates, items: [] };
    data.forEach((item) => {
      if (item.resourceCode) {
        rates.set(item.resourceCode.trim().toUpperCase(), item.nettRate);
      }
    });
    return { rates, items: data };
  }, [data, showColumnCodes]);
}

/**
 * Resolve a code (or a composite "A+B" code that sums two rates) into its
 * total rate.  For composite codes, parts that are absent from the library are
 * skipped (partial sum) so the badge always shows whatever rate IS available.
 * Returns `undefined` only when no part resolves at all.
 */
function lookupRate(code: string, rates: Map<string, number>): number | undefined {
  if (!code || code === '[dynamic]') return undefined;
  const parts = code.split('+').map((p) => p.trim()).filter(Boolean);
  if (parts.length === 0) return undefined;
  let total = 0;
  let found = 0;
  for (const part of parts) {
    const rate = rates.get(part.toUpperCase());
    if (rate !== undefined) {
      total += rate;
      found++;
    }
  }
  return found > 0 ? total : undefined;
}

/**
 * Renders the dev-mode code + rate beneath a column header. Self-gates on
 * `useDevMode()` (rather than the caller conditionally including it) so the
 * navbar dev-mode switch can toggle it live without every `addColumnCodes`
 * call site needing to re-run its own `columns` memo.
 */
const ColumnCodeBadge: React.FC<{ code: string; selectable?: CodeSelectConfig; colKey?: string }> = ({ code, selectable, colKey }) => {
  const showColumnCodes = useDevMode();
  const { rates, items } = useDevModeCodeLibrary();
  const rate = lookupRate(code, rates);
  const options = useMemo(
    () =>
      selectable
        ? items.map((i) => ({
            value: i.resourceCode,
            label: `${i.resourceCode} — ${i.resourceDescription ?? ''}`,
          }))
        : [],
    [items, selectable],
  );
  if (!showColumnCodes) return null;
  return (
    <div style={selectable ? { minHeight: 42 } : undefined}>
      {selectable && colKey ? (
        <Select
          size="small"
          showSearch
          allowClear={!!selectable.overrides[colKey]}
          value={code || undefined}
          placeholder="Select code"
          options={options.some((o) => o.value.toUpperCase() === code.toUpperCase()) ? options : [{ value: code, label: code }, ...options]}
          optionFilterProp="label"
          popupMatchSelectWidth={420}
          style={{ width: '100%', minWidth: 110, marginTop: 2, fontSize: 10 }}
          disabled={selectable.disabled}
          onClick={(e) => e.stopPropagation()}
          onChange={(v?: string) => selectable.onChange(colKey, v ?? null)}
          labelRender={() => <span style={{ fontSize: 10, color: selectable.overrides[colKey] ? '#d46b08' : '#999' }}>{code}</span>}
        />
      ) : (
      <div style={{ fontSize: 10, color: '#999', fontWeight: 500, marginTop: 2, lineHeight: 1.3, letterSpacing: '0.01em' }}>
        {code}
      </div>
      )}
      {rate !== undefined && (
        <div style={{ fontSize: 10, color: '#16a34a', fontWeight: 600, lineHeight: 1.3 }}>
          £{rate.toFixed(2)}
        </div>
      )}
    </div>
  );
};

/**
 * Column header wrapper. The fixed header height for the selectable (Plot
 * Drives) variant only applies while dev mode is on — otherwise the code
 * badge is hidden and reserving that space would leave a blank gap.
 */
const ColumnHeaderTitle: React.FC<{ title: React.ReactNode; code: string; selectable?: CodeSelectConfig; colKey?: string }> = ({ title, code, selectable, colKey }) => {
  const showColumnCodes = useDevMode();
  const reserveSpace = !!selectable && showColumnCodes;
  return (
    <div
      style={{
        textAlign: 'center',
        // Selectable: same fixed height for every column, title on top and the
        // dropdown pinned to the bottom, so all dropdowns share one line.
        ...(reserveSpace && { display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 116 }),
      }}
    >
      <div style={reserveSpace ? { flex: 1, marginTop: 3 } : undefined}>{title}</div>
      <ColumnCodeBadge code={code} selectable={selectable} colKey={colKey} />
    </div>
  );
};

/** Makes the dev-mode code badge a templates-API picker. `null` clears the override. */
export interface CodeSelectConfig {
  overrides: Record<string, string>;
  onChange: (colKey: string, code: string | null) => void;
  disabled?: boolean;
}

type AnyCol<T> = NonNullable<TableProps<T>['columns']>[number];

function processColumn<T extends object>(
  col: AnyCol<T>,
  references: Record<string, { code?: string; dimensionValueKey?: string }>,
  dynamicCodes?: Record<string, string>,
  selectable?: CodeSelectConfig,
): AnyCol<T> {
  if ('children' in col && Array.isArray(col.children)) {
    return { ...col, children: addColumnCodes(col.children as NonNullable<TableProps<T>['columns']>, references, dynamicCodes, selectable) };
  }
  const key = (('dataIndex' in col ? col.dataIndex : undefined) as string) ?? (col.key as string);
  const ref = key ? references[key] : undefined;
  const override = key ? selectable?.overrides[key] : undefined;
  const code = override ?? ref?.code
    ?? (ref?.dimensionValueKey && dynamicCodes ? dynamicCodes[ref.dimensionValueKey] : undefined);
  if (!code && !(selectable && ref)) return col;
  return {
    ...col,
    title: <ColumnHeaderTitle title={col.title as React.ReactNode} code={code ?? ''} selectable={selectable} colKey={key} />,
  };
}

export function addColumnCodes<T extends object>(
  columns: NonNullable<TableProps<T>['columns']>,
  references: Record<string, { code?: string; dimensionValueKey?: string }>,
  dynamicCodes?: Record<string, string>,
  selectable?: CodeSelectConfig,
): NonNullable<TableProps<T>['columns']> {
  return columns.map((col) => processColumn(col, references, dynamicCodes, selectable));
}
